import { jwtDecode } from 'jwt-decode';
import { create } from 'zustand';

import {
  getFromLocalStorage,
  setToLocalStorage,
  removeFromLocalStorage,
} from '@/services/local.storage';

type DecodedToken = {
  id?: string;
  email?: string;
  actionType?: string;
  exp?: number;
};

type AuthState = {
  isInitialized: boolean;
  isLoggedIn: boolean;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  role: string;
  userId: string | null;
  login: (
    token: string,
    firstName: string,
    lastName: string,
    role: string
  ) => void;
  logout: () => void;
  initialize: () => void;
};

export const UserRoles = {
  ADMIN: 'admin',
  GOLFER: 'golfer',
  CORPORATE: 'corporate',
} as const;

const getUserIdFromToken = (token: string | null): string | null => {
  if (!token) {
    return null;
  }
  try {
    const decoded: DecodedToken = jwtDecode(token);
    return decoded.id ?? null;
  } catch {
    return null;
  }
};

const isAccessToken = (token: string | null): boolean => {
  if (!token) {
    return false;
  }
  try {
    const decoded: DecodedToken = jwtDecode(token);
    return !decoded.actionType;
  } catch {
    return false;
  }
};

export const useAuthStore = create<AuthState>((set) => {
  return {
    isInitialized: false,
    isLoggedIn: false,
    firstName: '',
    lastName: '',
    role: '',
    isAdmin: false,
    userId: null,

    login: (accessToken, firstName, lastName, role): void => {
      const userId = getUserIdFromToken(accessToken);

      setToLocalStorage('token', accessToken);
      setToLocalStorage('firstName', firstName);
      setToLocalStorage('lastName', lastName);
      setToLocalStorage('role', role);
      if (userId) {
        setToLocalStorage('userId', userId);
      }

      set({
        isLoggedIn: isAccessToken(accessToken),
        firstName,
        lastName,
        role,
        isAdmin: role === 'admin',
        userId,
      });
    },

    logout: (): void => {
      removeFromLocalStorage('token');
      removeFromLocalStorage('firstName');
      removeFromLocalStorage('lastName');
      removeFromLocalStorage('role');
      removeFromLocalStorage('userId');

      set({
        isLoggedIn: false,
        firstName: '',
        lastName: '',
        role: '',
        isAdmin: false,
        userId: null,
      });
    },

    initialize: (): void => {
      const token = getFromLocalStorage('token');
      const valid = isAccessToken(token);
      const firstName = getFromLocalStorage('firstName') || '';
      const lastName = getFromLocalStorage('lastName') || '';
      const role = getFromLocalStorage('role');
      const userId = getFromLocalStorage('userId') || getUserIdFromToken(token);

      set({
        isInitialized: true,
        isLoggedIn: valid,
        firstName,
        lastName,
        role,
        isAdmin: valid && role === 'admin',
        userId,
      });
    },
  };
});
